(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.LifeforceLeadTime = api;
    api.init(root.document);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[–—\-/()]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isNaturalFinish(value) {
    const finish = normalize(value);

    if (!finish) return true;
    if (finish.includes('no paint strip')) return true;

    const extendedTerms = [
      'paint',
      'sprayed',
      'spray',
      'stain',
      'two-tone',
      'two tone',
      'colour',
      'color',
      'white',
      'grey',
      'gray',
      'black',
      'aged',
      'rubio',
      'wash',
    ];

    if (extendedTerms.some((term) => finish.includes(term))) return false;

    const naturalTerms = [
      'clear sealer',
      'clear water-base sealer',
      'clear water base sealer',
      'water-base clear sealer',
      'water base clear sealer',
      'water-base sealer',
      'water base sealer',
      'sealed with water-base',
      'sealed with water base',
      'natural wood sealed',
      'pinewood',
      'beechwood',
    ];

    return naturalTerms.some((term) => finish.includes(term));
  }

  function calculateRange(baseMin, baseMax, finishValue, finishMin, finishMax) {
    if (baseMin === '' || baseMin == null || baseMax === '' || baseMax == null) {
      return null;
    }

    const min = Number(baseMin);
    const max = Number(baseMax);

    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

    const adjusted = finishValue != null && finishValue !== '' && !isNaturalFinish(finishValue);
    const explicitFinishMin = Number(finishMin);
    const explicitFinishMax = Number(finishMax);
    const hasExplicitFinishRange = Number.isFinite(explicitFinishMin) && Number.isFinite(explicitFinishMax);
    const resultMin = adjusted && hasExplicitFinishRange ? explicitFinishMin : min;
    const resultMax = adjusted && hasExplicitFinishRange ? explicitFinishMax : max;

    return {
      min: resultMin,
      max: resultMax,
      label: `${resultMin}–${resultMax} weeks`,
      adjusted,
    };
  }

  function findFinishOptionIndex(optionNames, finishOptionName) {
    const expected = normalize(finishOptionName);
    if (!expected) return -1;
    return (optionNames || []).findIndex((name) => normalize(name) === expected);
  }

  function calculateVariantRange(config, variantId) {
    if (!config || !Array.isArray(config.variants)) return null;

    const variant = config.variants.find((candidate) => String(candidate.id) === String(variantId));
    if (!variant) return null;

    const finishOptionIndex = findFinishOptionIndex(config.optionNames, config.finishOptionName);
    const finishValue = finishOptionIndex >= 0 ? variant.options[finishOptionIndex] : null;

    return calculateRange(config.baseMin, config.baseMax, finishValue, config.finishMin, config.finishMax);
  }

  function syncFormProperties(form) {
    if (!form || typeof form.querySelector !== 'function') return null;

    const configNode = form.querySelector('[data-lead-time-config]');
    const variantIdInput = form.querySelector('input[name="id"]');
    const labelInput = form.querySelector('[data-lead-time-label]');
    const minInput = form.querySelector('[data-lead-time-min]');
    const maxInput = form.querySelector('[data-lead-time-max]');

    if (!configNode || !labelInput || !minInput || !maxInput) return null;

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (_error) {
      return null;
    }

    let range = variantIdInput && variantIdInput.value
      ? calculateVariantRange(config, variantIdInput.value)
      : null;

    if (!range) {
      const finishOptionIndex = findFinishOptionIndex(config.optionNames, config.finishOptionName);
      let finishValue = null;

      if (finishOptionIndex >= 0) {
        const scope = form.closest && form.closest('.shopify-section')
          ? form.closest('.shopify-section')
          : form;
        const expectedName = `options[${config.optionNames[finishOptionIndex]}]`;
        const controls = scope && scope.querySelectorAll
          ? Array.from(scope.querySelectorAll('[name^="options["]'))
          : [];
        const matches = controls.filter((control) => control.name === expectedName);
        const selected = matches.find((control) => control.tagName === 'SELECT' || control.checked) || matches[0];
        finishValue = selected ? selected.value : null;
      }

      range = calculateRange(config.baseMin, config.baseMax, finishValue, config.finishMin, config.finishMax);
    }

    if (!range) return null;

    labelInput.value = range.label;
    minInput.value = String(range.min);
    maxInput.value = String(range.max);
    if (labelInput.setAttribute) labelInput.setAttribute('value', range.label);
    if (minInput.setAttribute) minInput.setAttribute('value', String(range.min));
    if (maxInput.setAttribute) maxInput.setAttribute('value', String(range.max));

    return range;
  }

  function init(documentRoot) {
    if (!documentRoot || documentRoot.__lifeforceLeadTimeInitialized) return;
    documentRoot.__lifeforceLeadTimeInitialized = true;

    const syncFormsWithin = (container) => {
      if (!container || typeof container.querySelectorAll !== 'function') return;
      container.querySelectorAll('form[data-lead-time-form]').forEach(syncFormProperties);
    };

    const syncAll = () => syncFormsWithin(documentRoot);

    const initialiseSyncs = () => {
      syncAll();
      [100, 500, 1500, 3500].forEach((delay) => setTimeout(syncAll, delay));
    };

    if (documentRoot.readyState === 'loading') {
      documentRoot.addEventListener('DOMContentLoaded', initialiseSyncs, { once: true });
    } else {
      initialiseSyncs();
    }

    documentRoot.addEventListener('change', (event) => {
      const section = event.target && event.target.closest
        ? event.target.closest('.shopify-section')
        : null;
      setTimeout(() => syncFormsWithin(section || documentRoot), 0);
    });

    documentRoot.addEventListener('input', (event) => {
      const section = event.target && event.target.closest
        ? event.target.closest('.shopify-section')
        : null;
      setTimeout(() => syncFormsWithin(section || documentRoot), 0);
    });

    documentRoot.addEventListener(
      'click',
      (event) => {
        const trigger = event.target && event.target.closest
          ? event.target.closest('.tpo_add-to-cart, .shopify-payment-button__button')
          : null;
        if (!trigger) return;
        const section = trigger.closest ? trigger.closest('.shopify-section') : null;
        syncFormsWithin(section || documentRoot);
      },
      true,
    );

    documentRoot.addEventListener(
      'submit',
      (event) => {
        if (event.target && event.target.matches && event.target.matches('form[data-lead-time-form]')) {
          syncFormProperties(event.target);
        }
      },
      true,
    );
  }

  return {
    isNaturalFinish,
    calculateRange,
    findFinishOptionIndex,
    calculateVariantRange,
    syncFormProperties,
    init,
  };
});
